import { connect } from 'react-redux';
import { sendGALinkClickEvent } from '@noths/polaris-client-google-analytics';
import { updateFavourite } from '@noths/polaris-client-user-favourites';
import getConfig from 'next/config';

import { ProductList } from 'src/components/organisms/ProductList/components/ProductList/ProductList';
import { productListClick } from 'src/components/organisms/ProductList/modules/actions';
import { selectReferredBy } from 'src/components/organisms/ProductList/modules/selectors';
import type {
  ProductListContainerDispatchProps,
  ProductListContainerOwnProps,
  ProductListContainerStateProps,
} from 'src/components/organisms/ProductList/types';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { PromoLabel } from 'src/redux/content/types';
import { selectPageType } from 'src/redux/navigation/selectors';
import { selectProviderAttributionToken } from 'src/redux/products/selectors/selectors';
import type { UpdateHistoryPayload } from 'src/redux/products/thunks/updatePageHistory';
import { updatePageHistory } from 'src/redux/products/thunks/updatePageHistory';
import type { AppThunkDispatch } from 'src/redux/store';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking/slice';
import { trackProductMediaInteraction } from 'src/tracking/events/productMediaInteraction';
import { getMiraklListingIdentity } from 'src/tracking/mirakl/getMiraklViewEvent';
import type { NextConfig } from 'src/types/nextConfig';
import { isPartnerPage } from 'src/utils/page';

const nextConfig = getConfig() as NextConfig;

// One reference for every render, so the card props memoised on it are not rebuilt.
const NO_PROMO_LABELS: PromoLabel[] = [];

const mapStateToProps = (
  state: ReduxApplicationState,
  ownProps: ProductListContainerOwnProps,
): ProductListContainerStateProps => {
  return {
    currency: state.user.currencyCode,
    inListingsCallout: (ownProps.page === 1 && state.content.inListingsCallout) || undefined,
    /* Read from the page type rather than from `content.partner`. State hydration
       merges rather than replaces (`R.mergeDeepRight` in `withStateHydration`) onto a
       singleton client store, so `content.partner` is never cleared and would survive
       a partner -> category soft navigation, suppressing the badge on a grid that is
       not partner scoped. Every page sets its own `pageType`, so that one is always
       overwritten. */
    isSinglePartnerListing: isPartnerPage(selectPageType(state)),
    miraklPageId: getMiraklListingIdentity(state)?.pageId,
    promoLabels: state.content.promoLabels ?? NO_PROMO_LABELS,
    referredBy: selectReferredBy(state),
    providerAttributionToken: selectProviderAttributionToken(state),
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch): ProductListContainerDispatchProps => {
  return {
    updateFavourite: ({ productCode }: { productCode: number }) =>
      dispatch(updateFavourite({ productCode, nextConfig })),
    updatePageHistory: (payload: UpdateHistoryPayload) => dispatch(updatePageHistory(payload)),
    onInListingsCalloutClick: (ev: React.MouseEvent<HTMLAnchorElement>) => {
      const url = new URL(ev.currentTarget.href);

      sendGALinkClickEvent(ev, {
        event: 'custom_event',
        event_action: 'CTA click',
        event_name: 'select_content',
        event_details: 'cta click',
        content_type: `in-listings callout|${url.pathname}`,
      });
    },
    /* Sent straight from here rather than dispatched through the events map, unlike
       the click below: the event describes only what the customer did to the card, so
       there is no page context or store product to assemble it from. */
    onMediaNavigate: trackProductMediaInteraction,
    onProductVisible: ({ productCode }: { productCode: number }) =>
      dispatch(visibilityTrackingActions.onProductVisible({ productCode })),
    onProductClick: ({
      destinationUrl,
      linkOpensInSameWindow,
      productCode,
      productUrl,
    }: {
      destinationUrl: string;
      linkOpensInSameWindow: boolean;
      productCode: number;
      productUrl: string;
    }) => {
      dispatch(visibilityTrackingActions.sendProductImpressionBatch());
      dispatch(
        productListClick({ productUrl, destinationUrl, productCode, linkOpensInSameWindow }),
      );
    },
  };
};

export const ProductListContainer = connect(mapStateToProps, mapDispatchToProps)(ProductList);
